import React, { useState, useEffect, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { dataService } from '../services/dataService';
import { confirmDialog, alertDialog } from '../utils/dialog';
import { parseCsv, toCsv, autoMapColumns, rowsToRecords } from '../utils/csv';
import {
  FILTER_OPS, applyView, visibleFields, groupForKanban, KANBAN_NONE,
} from '../utils/tableViews';
import {
  typeIcon, STATUS_COLORS,
  FieldRow, RecordCell, RecordCtxMenu, LinkPicker,
} from './Collections/parts';

// Standalone Tables section: the workspace's collections as a first-class,
// day-to-day data workspace (grid + kanban + saved views, record detail,
// CSV import/export) — no pipeline canvas required. Schemas and saved views
// live on the pipeline doc's collection nodes and records in each
// collection's subcollection, so pipelines keep triggering off exactly the
// same data.

const CREATE_FIELD = '__create__';
const runManualTriggerFn = httpsCallable(functions, 'runManualTrigger');
const createPublicFormFn = httpsCallable(functions, 'createPublicForm');
const setPublicFormEnabledFn = httpsCallable(functions, 'setPublicFormEnabled');
const deletePublicFormFn = httpsCallable(functions, 'deletePublicForm');

// Public URL for a form: pretty hosting rewrite in production, the functions
// emulator origin during e2e/local runs.
function formPublicUrl(formId, key) {
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    return `http://localhost:5001/projectly-dev/us-central1/publicForm/${formId}/${key}`;
  }
  return `https://projectly-dev.web.app/f/${formId}/${key}`;
}

function fmtTs(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    if (!d || isNaN(d.getTime())) return null;
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}

// ── CSV import modal ─────────────────────────────────────────────────────────
function ImportModal({ parsed, fields, onImport, onClose }) {
  const [mapping, setMapping] = useState(() =>
    autoMapColumns(parsed.headers, fields).map(m => ({ ...m, fieldId: m.fieldId || '' }))
  );
  const [importing, setImporting] = useState(false);

  const setCol = (i, fieldId) =>
    setMapping(prev => prev.map((m, j) => (j === i ? { ...m, fieldId } : m)));

  const mappedCount = mapping.filter(m => m.fieldId).length;

  const run = async () => {
    setImporting(true);
    try { await onImport(mapping); } finally { setImporting(false); }
  };

  return (
    <div className="cm-backdrop" onClick={onClose}>
      <div className="tv-import-modal" onClick={e => e.stopPropagation()}>
        <div className="cm-hdr">
          <span className="cm-hdr-icon">📥</span>
          <span className="tv-import-title">Import CSV — map columns to fields</span>
          <button className="cm-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="tv-import-body">
          <p className="tv-import-hint">
            {parsed.rows.length} row{parsed.rows.length !== 1 ? 's' : ''} found. Choose which field each CSV
            column goes into — or create a new field, or skip the column.
          </p>
          <table className="tv-import-table">
            <thead>
              <tr><th>CSV column</th><th>Example</th><th>Import into</th></tr>
            </thead>
            <tbody>
              {mapping.map((m, i) => (
                <tr key={i}>
                  <td className="tv-import-col">{m.header || <em>(unnamed)</em>}</td>
                  <td className="tv-import-example">{String(parsed.rows[0]?.[i] ?? '')}</td>
                  <td>
                    <select className="cm-cell-sel" value={m.fieldId} onChange={e => setCol(i, e.target.value)}>
                      <option value="">— skip —</option>
                      {fields.map(f => (
                        <option key={f.id} value={f.id}>{typeIcon(f.type)} {f.name}</option>
                      ))}
                      <option value={CREATE_FIELD}>＋ Create new field "{m.header}"</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tv-import-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            data-testid="tv-import-run"
            disabled={importing || mappedCount === 0}
            onClick={run}
          >
            {importing ? 'Importing…' : `Import ${parsed.rows.length} rows`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Public forms modal ───────────────────────────────────────────────────────
function FormsModal({ workspaceId, collection, fields, onClose }) {
  const [forms, setForms] = useState(null); // null = loading
  const [building, setBuilding] = useState(false);
  const [title, setTitle] = useState(collection.data?.label || 'Form');
  const [description, setDescription] = useState('');
  const [thankYou, setThankYou] = useState('');
  const [picked, setPicked] = useState(() =>
    // default: all fields on, first field required
    fields.map((f, i) => ({ fieldId: f.id, label: f.name, type: f.type === 'select' ? 'select' : f.type, options: f.options, on: true, required: i === 0 }))
  );
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const unsub = dataService.subscribePublicForms(workspaceId, collection.id, setForms);
    return () => unsub && unsub();
  }, [workspaceId, collection.id]);

  const setPick = (i, updates) =>
    setPicked(prev => prev.map((p, j) => (j === i ? { ...p, ...updates } : p)));

  const create = async () => {
    const chosen = picked.filter(p => p.on).map(({ fieldId, label, type, options, required }) => ({
      fieldId, label, type, required, ...(options ? { options } : {}),
    }));
    if (!chosen.length) return;
    setCreating(true);
    try {
      await createPublicFormFn({
        workspaceId, collectionId: collection.id,
        title: title.trim() || 'Form', description: description.trim(), thankYou: thankYou.trim(),
        fields: chosen,
      });
      setBuilding(false);
    } catch (e) {
      await alertDialog({ title: 'Could not create form', message: e?.message || 'Something went wrong.' });
    } finally {
      setCreating(false);
    }
  };

  const copy = async (form) => {
    const url = formPublicUrl(form.id, form.key);
    try { await navigator.clipboard.writeText(url); setCopied(form.id); setTimeout(() => setCopied(null), 1500); }
    catch { window.prompt('Copy the form link:', url); }
  };

  const toggle = (form) => setPublicFormEnabledFn({ formId: form.id, enabled: form.enabled === false });
  const remove = async (form) => {
    if (!(await confirmDialog({ title: 'Delete form', message: `Delete "${form.title}"? The link stops working immediately. Submitted records are kept.`, confirmText: 'Delete', danger: true }))) return;
    await deletePublicFormFn({ formId: form.id });
  };

  return (
    <div className="cm-backdrop" onClick={onClose}>
      <div className="tv-import-modal tv-forms-modal" onClick={e => e.stopPropagation()}>
        <div className="cm-hdr">
          <span className="cm-hdr-icon">🔗</span>
          <span className="tv-import-title">Online forms — {collection.data?.label}</span>
          <button className="cm-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="tv-import-body">
          <p className="tv-import-hint">
            Share a form link and every submission becomes a record in this table — which means
            your pipelines run on it (a <em>record added</em> trigger fires per submission).
            No login needed to submit.
          </p>

          {forms === null ? (
            <div className="tv-forms-loading">Loading…</div>
          ) : forms.length === 0 && !building ? (
            <div className="tv-forms-empty">No forms yet for this table.</div>
          ) : (
            forms.map(form => (
              <div key={form.id} className={`tv-form-row ${form.enabled === false ? 'off' : ''}`} data-testid={`tv-form-${form.id}`}>
                <div className="tv-form-row-main">
                  <span className="tv-form-title">{form.title}</span>
                  <span className="tv-form-meta">
                    {form.submitCount || 0} submission{(form.submitCount || 0) !== 1 ? 's' : ''}
                    {form.enabled === false ? ' · closed' : ''}
                  </span>
                </div>
                <div className="tv-form-row-actions">
                  <button className="btn-secondary tv-form-btn" data-testid={`tv-form-copy-${form.id}`} onClick={() => copy(form)}>
                    {copied === form.id ? '✓ Copied' : '🔗 Copy link'}
                  </button>
                  <a className="btn-secondary tv-form-btn" href={formPublicUrl(form.id, form.key)} target="_blank" rel="noreferrer">↗ Open</a>
                  <button className="btn-secondary tv-form-btn" onClick={() => toggle(form)}>
                    {form.enabled === false ? 'Reopen' : 'Close'}
                  </button>
                  <button className="cm-field-del-btn" onClick={() => remove(form)} title="Delete form">✕</button>
                </div>
              </div>
            ))
          )}

          {building ? (
            <div className="tv-form-builder" data-testid="tv-form-builder">
              <div className="tv-ve-row">
                <label>Form title</label>
                <input data-testid="tv-form-title" className="tv-rail-new-input" value={title} onChange={e => setTitle(e.target.value)} />
              </div>
              <div className="tv-ve-row">
                <label>Description <span className="tv-ve-hint">(optional, shown under the title)</span></label>
                <input className="tv-rail-new-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Tell us what you need…" />
              </div>
              <div className="tv-ve-row">
                <label>Thank-you message <span className="tv-ve-hint">(optional)</span></label>
                <input className="tv-rail-new-input" value={thankYou} onChange={e => setThankYou(e.target.value)} placeholder="Thanks — we'll be in touch!" />
              </div>
              <div className="tv-ve-row">
                <label>Fields on the form</label>
                {picked.map((p, i) => (
                  <div key={p.fieldId} className="tv-form-fieldrow">
                    <label className="tv-ve-fieldchk">
                      <input type="checkbox" checked={p.on} onChange={() => setPick(i, { on: !p.on })} />
                    </label>
                    <input
                      className="tv-rail-new-input"
                      value={p.label}
                      disabled={!p.on}
                      onChange={e => setPick(i, { label: e.target.value })}
                      title="Label shown on the form"
                    />
                    <label className={`tv-ve-fieldchk tv-form-req ${!p.on ? 'off' : ''}`}>
                      <input type="checkbox" checked={p.required} disabled={!p.on} onChange={() => setPick(i, { required: !p.required })} />
                      required
                    </label>
                  </div>
                ))}
              </div>
              <div className="tv-import-actions" style={{ padding: 0, borderTop: 'none' }}>
                <button className="btn-secondary" onClick={() => setBuilding(false)}>Cancel</button>
                <button
                  data-testid="tv-form-create"
                  className="btn-primary"
                  disabled={creating || !picked.some(p => p.on)}
                  onClick={create}
                >{creating ? 'Creating…' : 'Create form'}</button>
              </div>
            </div>
          ) : (
            <button data-testid="tv-form-new" className="btn-primary tv-form-newbtn" onClick={() => setBuilding(true)} disabled={fields.length === 0}>
              + New form
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Saved-view editor modal ──────────────────────────────────────────────────
function ViewEditor({ view, fields, onSave, onDelete, onClose }) {
  const [name, setName] = useState(view.name || '');
  const [type, setType] = useState(view.type || 'grid');
  const [groupBy, setGroupBy] = useState(view.groupBy || '');
  const [filters, setFilters] = useState(view.filters || []);
  const [hidden, setHidden] = useState(new Set(view.hiddenFieldIds || []));

  const selectFields = fields.filter(f => f.type === 'select');

  const setFilter = (i, updates) =>
    setFilters(prev => prev.map((f, j) => (j === i ? { ...f, ...updates } : f)));
  const addFilter = () => setFilters(prev => [...prev, { fieldId: fields[0]?.id || '', op: 'is', value: '' }]);
  const removeFilter = (i) => setFilters(prev => prev.filter((_, j) => j !== i));
  const toggleHidden = (fId) =>
    setHidden(prev => { const n = new Set(prev); n.has(fId) ? n.delete(fId) : n.add(fId); return n; });

  const canSave = name.trim() && (type !== 'kanban' || groupBy);

  const save = () => onSave({
    ...view,
    name: name.trim(),
    type,
    groupBy: type === 'kanban' ? groupBy : (view.groupBy || null),
    filters: filters.filter(f => f.fieldId && f.op),
    hiddenFieldIds: [...hidden],
  });

  return (
    <div className="cm-backdrop" onClick={onClose}>
      <div className="tv-import-modal tv-view-editor" onClick={e => e.stopPropagation()}>
        <div className="cm-hdr">
          <span className="cm-hdr-icon">👁️</span>
          <span className="tv-import-title">{view.id ? 'Edit view' : 'New view'}</span>
          <button className="cm-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="tv-import-body">
          <div className="tv-ve-row">
            <label>View name</label>
            <input
              data-testid="tv-view-name"
              className="tv-rail-new-input"
              placeholder="e.g. Open orders"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="tv-ve-row">
            <label>Layout</label>
            <div className="tv-ve-type">
              <button className={`btn-secondary ${type === 'grid' ? 'active' : ''}`} onClick={() => setType('grid')}>▦ Grid</button>
              <button data-testid="tv-view-kanban" className={`btn-secondary ${type === 'kanban' ? 'active' : ''}`} onClick={() => setType('kanban')}>📌 Kanban</button>
            </div>
          </div>
          {type === 'kanban' && (
            <div className="tv-ve-row">
              <label>Group cards by</label>
              {selectFields.length === 0 ? (
                <span className="tv-ve-hint">Kanban needs a <strong>Select</strong> field (e.g. Status). Add one in ⚙️ Fields first.</span>
              ) : (
                <select data-testid="tv-view-groupby" className="cm-cell-sel" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                  <option value="">— choose select field —</option>
                  {selectFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              )}
            </div>
          )}
          <div className="tv-ve-row">
            <label>Filters <span className="tv-ve-hint">(all must match)</span></label>
            {filters.map((f, i) => (
              <div key={i} className="tv-ve-filter">
                <select className="cm-cell-sel" value={f.fieldId} onChange={e => setFilter(i, { fieldId: e.target.value })}>
                  {fields.map(fd => <option key={fd.id} value={fd.id}>{fd.name}</option>)}
                </select>
                <select className="cm-cell-sel" value={f.op} onChange={e => setFilter(i, { op: e.target.value })}>
                  {FILTER_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {!['empty', 'not_empty'].includes(f.op) && (
                  <input className="tv-rail-new-input" placeholder="value" value={f.value || ''} onChange={e => setFilter(i, { value: e.target.value })} />
                )}
                <button className="cm-field-del-btn" onClick={() => removeFilter(i)} title="Remove filter">✕</button>
              </div>
            ))}
            <button className="btn-secondary tv-ve-addfilter" onClick={addFilter}>+ Add filter</button>
          </div>
          <div className="tv-ve-row">
            <label>Visible fields</label>
            <div className="tv-ve-fields">
              {fields.map(f => (
                <label key={f.id} className="tv-ve-fieldchk">
                  <input type="checkbox" checked={!hidden.has(f.id)} onChange={() => toggleHidden(f.id)} />
                  {typeIcon(f.type)} {f.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="tv-import-actions">
          {view.id && (
            <button className="btn-danger tv-ve-delete" onClick={() => onDelete(view.id)}>Delete view</button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button data-testid="tv-view-save" className="btn-primary" disabled={!canSave} onClick={save}>Save view</button>
        </div>
      </div>
    </div>
  );
}

// ── Kanban board ─────────────────────────────────────────────────────────────
function KanbanBoard({ records, fields, groupBy, onMove, onOpen }) {
  const groupField = fields.find(f => f.id === groupBy);
  const cols = groupForKanban(records, groupBy, groupField);
  const titleField = fields.find(f => f.type === 'text') || fields[0];
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const moveTargets = (groupField?.options || []);

  return (
    <div className="tv-kanban" data-testid="tv-kanban">
      {cols.map(col => (
        <div
          key={col.key}
          className={`tv-kb-col ${overCol === col.key ? 'tv-kb-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setOverCol(col.key); }}
          onDragLeave={() => setOverCol(prev => (prev === col.key ? null : prev))}
          onDrop={e => {
            e.preventDefault();
            setOverCol(null);
            const rid = e.dataTransfer.getData('text/plain') || dragId;
            if (rid) onMove(rid, col.key === KANBAN_NONE ? '' : col.key);
            setDragId(null);
          }}
        >
          <div className="tv-kb-col-hdr">
            <span className="tv-kb-col-name" style={STATUS_COLORS[col.label] ? { color: STATUS_COLORS[col.label] } : {}}>
              {col.label}
            </span>
            <span className="tv-kb-col-count">{col.records.length}</span>
          </div>
          <div className="tv-kb-cards">
            {col.records.map(r => (
              <div
                key={r.id}
                className="tv-kb-card"
                draggable
                onDragStart={e => { e.dataTransfer.setData('text/plain', r.id); setDragId(r.id); }}
                onDragEnd={() => setDragId(null)}
                onDoubleClick={() => onOpen(r.id)}
                title="Drag to move · double-click to open"
              >
                <div className="tv-kb-card-title">
                  {titleField ? (r.values?.[titleField.id] || '(untitled)') : r.id}
                </div>
                <div className="tv-kb-card-meta">
                  {fields.filter(f => f.id !== titleField?.id && f.id !== groupBy).slice(0, 2).map(f => {
                    const v = r.values?.[f.id];
                    return v ? <span key={f.id} className="tv-kb-chip">{String(v).slice(0, 24)}</span> : null;
                  })}
                </div>
                {/* Accessible / mobile / e2e-friendly move control (drag alternative) */}
                <select
                  className="tv-kb-move"
                  data-testid={`tv-kb-move-${r.id}`}
                  value={String(r.values?.[groupBy] ?? '')}
                  onChange={e => onMove(r.id, e.target.value)}
                  onClick={e => e.stopPropagation()}
                  title="Move to…"
                >
                  <option value="">(none)</option>
                  {moveTargets.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            {col.records.length === 0 && <div className="tv-kb-empty">—</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Record detail drawer ─────────────────────────────────────────────────────
function RecordDetail({ record, fields, allCollections, recordsByCol, manualTriggers, onRun, onChange, onDelete, onClose }) {
  const created = fmtTs(record.createdAt);
  const updated = fmtTs(record.updatedAt);
  const [running, setRunning] = useState(null);
  return (
    <div className="tv-detail" data-testid="tv-detail">
      <div className="tv-detail-hdr">
        <span className="tv-detail-title">Record</span>
        <span className="tv-detail-id">#{record.id.slice(-6)}</span>
        <button className="cm-close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="tv-detail-body">
        {fields.map(f => (
          <div key={f.id} className="tv-detail-field">
            <label className="tv-detail-label">{typeIcon(f.type)} {f.name}</label>
            <RecordCell
              field={f}
              value={record.values?.[f.id]}
              allCollections={allCollections}
              recordsByCol={recordsByCol}
              onChange={val => onChange(f.id, val)}
            />
          </div>
        ))}
        {manualTriggers.length > 0 && (
          <div className="tv-detail-run">
            <div className="tv-detail-label">⚡ Run a pipeline on this record</div>
            {manualTriggers.map(t => (
              <button
                key={t.id}
                className="btn-secondary tv-detail-run-btn"
                disabled={running === t.id}
                onClick={async () => {
                  setRunning(t.id);
                  try { await onRun(t.id, record.id); } finally { setRunning(null); }
                }}
              >
                {running === t.id ? 'Running…' : `▶ ${t.data?.label || 'Manual trigger'}`}
              </button>
            ))}
          </div>
        )}
        <div className="tv-detail-meta">
          {created && <div>Created: {created}</div>}
          {updated && <div>Updated: {updated}</div>}
          {record._lastModifiedBy === 'workflow_engine' && <div>Last changed by: ⚙️ a pipeline</div>}
        </div>
        <button className="btn-danger tv-detail-delete" onClick={() => onDelete(record.id)}>
          🗑️ Delete record
        </button>
      </div>
    </div>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────
export default function TablesView({ workspaceId, onNavigate }) {
  const [collections, setCollections] = useState([]);
  const [triggers, setTriggers] = useState([]);        // trigger nodes (for run-on-record + automate)
  const [edges, setEdges] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsByCol, setRecordsByCol] = useState({});
  const [sort, setSort] = useState(null);
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [showFields, setShowFields] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [importParsed, setImportParsed] = useState(null);
  const [recordCtx, setRecordCtx] = useState(null);
  const [linkPicker, setLinkPicker] = useState(null);
  const [addingRec, setAddingRec] = useState(false);
  const [newRec, setNewRec] = useState({});
  const [activeViewId, setActiveViewId] = useState(null); // null = default "All records" grid
  const [editorView, setEditorView] = useState(null);     // view being created/edited
  const [showForms, setShowForms] = useState(false);      // public forms modal
  const fileRef = useRef(null);

  const [nfName, setNfName] = useState('');
  const [nfType, setNfType] = useState('text');

  // Recent local node edits (create/rename/fields/views), re-applied over
  // incoming snapshots for a short window. Our writes are awaited — the doc
  // is correct server-side — but a listener can still deliver a late/stale
  // snapshot that would otherwise wipe a just-created table from the UI.
  const recentEditsRef = useRef({}); // nodeId -> { node, ts }

  const rememberEdit = (node) => {
    recentEditsRef.current[node.id] = { node, ts: Date.now() };
  };

  const applyRecentEdits = (nodes) => {
    const now = Date.now();
    let out = nodes;
    for (const [id, e] of Object.entries(recentEditsRef.current)) {
      if (now - e.ts > 15_000) { delete recentEditsRef.current[id]; continue; }
      const idx = out.findIndex(n => n.id === id);
      if (idx === -1) {
        out = [...out, e.node];
      } else {
        out = out.map(n => (n.id === id ? { ...n, data: { ...n.data, ...e.node.data } } : n));
      }
    }
    return out;
  };

  useEffect(() => {
    if (!workspaceId) return;
    setSelectedId(null); setRecords([]); setLoaded(false);
    const unsub = dataService.subscribePipeline(workspaceId, (p) => {
      const nodes = applyRecentEdits(p.nodes || []);
      setCollections(nodes.filter(n => n.type === 'collectionNode'));
      setTriggers(nodes.filter(n => n.type === 'triggerNode'));
      setEdges(p.edges || []);
      setLoaded(true);
      setSelectedId(prev => {
        const cols = nodes.filter(n => n.type === 'collectionNode');
        return prev && cols.some(c => c.id === prev) ? prev : (cols[0]?.id || null);
      });
    });
    dataService.getPipeline(workspaceId).then(() => setLoaded(true));
    return () => unsub && unsub();
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!workspaceId || !selectedId) { setRecords([]); return; }
    const unsub = dataService.subscribeRecords(workspaceId, selectedId, setRecords);
    return () => unsub && unsub();
  }, [workspaceId, selectedId]);

  const selected = collections.find(c => c.id === selectedId);
  const fields = useMemo(() => selected?.data?.fields || [], [selected]);
  const views = useMemo(() => selected?.data?.views || [], [selected]);
  const otherCollections = collections.filter(c => c.id !== selectedId);

  const activeView = views.find(v => v.id === activeViewId) || null;
  const shownFields = useMemo(() => visibleFields(fields, activeView), [fields, activeView]);

  // Manual triggers relevant to this collection: wired by triggerCollectionId
  // or by an edge from the collection node.
  const manualTriggers = useMemo(() => triggers.filter(t =>
    t.data?.triggerType === 'manual' && (
      t.data?.triggerCollectionId === selectedId ||
      edges.some(e => e.source === selectedId && e.target === t.id)
    )
  ), [triggers, edges, selectedId]);

  const relTargetKey = fields
    .filter(f => f.type === 'relation' && f.targetCollectionId)
    .map(f => f.targetCollectionId).sort().join(',');
  useEffect(() => {
    if (!workspaceId || !relTargetKey) return;
    const ids = relTargetKey.split(',');
    const unsubs = ids.map(colId =>
      dataService.subscribeRecords(workspaceId, colId, recs =>
        setRecordsByCol(prev => ({ ...prev, [colId]: recs }))
      )
    );
    return () => unsubs.forEach(u => u && u());
  }, [workspaceId, relTargetKey]);

  // View pipeline: saved view (filters+sort) → search → header sort override.
  const visibleRecords = useMemo(() => {
    let list = applyView(records, activeView, fields);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r =>
        Object.values(r.values || {}).some(v => String(v ?? '').toLowerCase().includes(q))
      );
    }
    if (sort) {
      const f = fields.find(x => x.id === sort.fieldId);
      list = [...list].sort((a, b) => {
        const av = a.values?.[sort.fieldId] ?? '';
        const bv = b.values?.[sort.fieldId] ?? '';
        if (f?.type === 'number') return (Number(av) - Number(bv)) * sort.dir;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * sort.dir;
      });
    }
    return list;
  }, [records, activeView, search, sort, fields]);

  const toggleSort = (fieldId) =>
    setSort(prev => (prev?.fieldId === fieldId
      ? (prev.dir === 1 ? { fieldId, dir: -1 } : null)
      : { fieldId, dir: 1 }));

  // ── Table ops ──
  const createTable = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const maxY = collections.reduce((m, c) => Math.max(m, c.position?.y || 0), 100);
      const node = {
        id: `col_${Date.now()}`,
        type: 'collectionNode',
        position: { x: 200, y: maxY + 160 },
        data: { label: name, fields: [] },
      };
      await dataService.addPipelineNode(workspaceId, node);
      // Echo locally so the table appears instantly even if the pipeline
      // snapshot listener lags; stale snapshots within the protection window
      // get the node re-applied by applyRecentEdits.
      rememberEdit(node);
      setCollections(prev => (prev.some(c => c.id === node.id) ? prev : [...prev, node]));
      setNewName('');
      setSelectedId(node.id);
      setShowFields(true);
    } finally {
      setCreating(false);
    }
  };

  // Apply a node-data change optimistically (instant UI) and persist it; the
  // pipeline snapshot reconciles afterwards.
  const patchNodeData = async (updates) => {
    const colId = selectedId;
    const current = collections.find(c => c.id === colId);
    if (current) rememberEdit({ ...current, data: { ...current.data, ...updates } });
    setCollections(prev => prev.map(c => (c.id === colId ? { ...c, data: { ...c.data, ...updates } } : c)));
    await dataService.updatePipelineNodeData(workspaceId, colId, updates);
  };

  const renameTable = async (label) => {
    if (!selected || !label.trim()) return;
    await patchNodeData({ label: label.trim() });
  };

  // ── Field ops ──
  const saveFields = (next) => patchNodeData({ fields: next });

  const addField = async () => {
    if (!nfName.trim()) return;
    const f = {
      id: `f${Date.now()}`,
      name: nfName.trim(),
      type: nfType,
      ...(nfType === 'select' ? { options: [] } : {}),
    };
    setNfName(''); setNfType('text');
    await saveFields([...fields, f]);
  };
  const updateField = (fId, updates) => saveFields(fields.map(f => (f.id === fId ? { ...f, ...updates } : f)));
  const deleteField = async (fId) => {
    if (!(await confirmDialog({ title: 'Delete field', message: 'Delete this field? Its values stay on records but will no longer be shown.', confirmText: 'Delete', danger: true }))) return;
    saveFields(fields.filter(f => f.id !== fId));
  };

  // ── View ops (persisted on the collection node) ──
  const saveViews = (next) => patchNodeData({ views: next });

  const saveView = async (view) => {
    let next;
    if (view.id) {
      next = views.map(v => (v.id === view.id ? view : v));
    } else {
      view = { ...view, id: `v${Date.now()}` };
      next = [...views, view];
    }
    await saveViews(next);
    setActiveViewId(view.id);
    setEditorView(null);
  };

  const deleteView = async (viewId) => {
    if (!(await confirmDialog({ title: 'Delete view', message: 'Delete this saved view? (Records are not affected.)', confirmText: 'Delete', danger: true }))) return;
    await saveViews(views.filter(v => v.id !== viewId));
    if (activeViewId === viewId) setActiveViewId(null);
    setEditorView(null);
  };

  // ── Record ops ──
  // A connector-backed collection mirrors another app. Editing a row here would
  // be silently reverted by the next sync — the remote value simply wins — so
  // the edit is refused with an explanation instead of appearing to work and
  // then vanishing a minute later. Pipelines still change this data; they just
  // do it by calling the other app's API, which is the only route that sticks.
  const connectorSource = selected?.data?.source?.type || null;
  const refuseRemoteEdit = async (what) => {
    await alertDialog({
      title: `This data lives in ${connectorSource}`,
      message: `${what} here would be overwritten the next time this collection syncs, because `
        + `${connectorSource} is the source of truth for these rows.\n\n`
        + `Change it in ${connectorSource} directly, or use a pipeline action that writes back to its API.`,
    });
  };

  const patchRecord = async (rId, fId, val) => {
    if (connectorSource) { await refuseRemoteEdit('Editing a value'); return; }
    return dataService.patchRecord(workspaceId, selectedId, rId, { [fId]: val });
  };
  const deleteRecord = async (rId) => {
    if (connectorSource) { await refuseRemoteEdit('Deleting a row'); return; }
    if (!(await confirmDialog({ title: 'Delete record', message: 'Delete this record? This cannot be undone.', confirmText: 'Delete', danger: true }))) return;
    await dataService.deleteRecord(workspaceId, selectedId, rId);
    if (detailId === rId) setDetailId(null);
  };
  const commitNewRecord = async () => {
    if (connectorSource) { await refuseRemoteEdit('Adding a row'); setAddingRec(false); return; }
    const hasValue = Object.values(newRec).some(v => v !== undefined && v !== null && String(v).trim() !== '');
    if (!hasValue) return;
    dataService.addRecord(workspaceId, selectedId, newRec);
    setNewRec({}); setAddingRec(false);
  };

  const runTriggerOnRecord = async (triggerNodeId, recordId) => {
    try {
      await runManualTriggerFn({ workspaceId, triggerNodeId, recordId });
      await alertDialog({ title: 'Pipeline run started', message: 'The pipeline ran against this record — check Logs (and your Inbox if it sends a message).' });
    } catch (e) {
      await alertDialog({ title: 'Run failed', message: e?.message || 'Could not run the pipeline.' });
    }
  };

  // ── CSV ──
  const exportCsv = () => {
    const headers = fields.map(f => f.name);
    const rows = records.map(r => fields.map(f => r.values?.[f.id] ?? ''));
    const blob = new Blob([toCsv(headers, rows)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(selected?.data?.label || 'table').replace(/[^\w-]+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onCsvPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);
    if (!parsed.headers.length || !parsed.rows.length) {
      await alertDialog({ title: 'Nothing to import', message: 'That CSV appears to be empty (needs a header row plus at least one data row).' });
      return;
    }
    setImportParsed(parsed);
  };

  const runImport = async (mapping) => {
    let nextFields = fields;
    const finalMapping = mapping.map(m => {
      if (m.fieldId !== CREATE_FIELD) return m;
      const f = { id: `f${Date.now()}_${m.index}`, name: m.header || `Column ${m.index + 1}`, type: 'text' };
      nextFields = [...nextFields, f];
      return { ...m, fieldId: f.id };
    });
    if (nextFields !== fields) await saveFields(nextFields);

    const recs = rowsToRecords(importParsed.rows, finalMapping.map(m => ({ ...m, fieldId: m.fieldId || null })));
    if (recs.length === 0) {
      await alertDialog({ title: 'Nothing to import', message: 'No rows had values in the mapped columns.' });
      return;
    }
    await dataService.seedRecords(workspaceId, selectedId, recs);
    setImportParsed(null);
    await alertDialog({ title: 'Import complete', message: `${recs.length} record${recs.length !== 1 ? 's' : ''} imported into ${selected?.data?.label || 'this table'}.` });
  };

  // ── Render ──
  if (!workspaceId) return <div className="pipeline-loading">No workspace selected.</div>;

  const detailRecord = detailId ? records.find(r => r.id === detailId) : null;
  const isKanban = activeView?.type === 'kanban' && activeView.groupBy;

  return (
    <div className="tables-view" data-testid="tables-view">
      {/* Left rail */}
      <div className="tv-rail">
        <div className="tv-rail-hdr">🗂️ Data</div>
        <div className="tv-rail-list">
          {collections.map(c => (
            <button
              key={c.id}
              data-testid={`tv-table-${c.id}`}
              className={`tv-rail-item ${c.id === selectedId ? 'active' : ''}`}
              onClick={() => { setSelectedId(c.id); setDetailId(null); setSort(null); setSearch(''); setAddingRec(false); setActiveViewId(null); }}
            >
              <span className="tv-rail-item-name">{c.data?.label || 'Untitled'}</span>
              <span className="tv-rail-item-count">{(c.data?.fields || []).length}f</span>
            </button>
          ))}
          {loaded && collections.length === 0 && (
            <div className="tv-rail-empty">No tables yet</div>
          )}
        </div>
        <div className="tv-rail-new">
          <input
            data-testid="tv-new-name"
            className="tv-rail-new-input"
            placeholder="New table name…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createTable()}
          />
          <button
            data-testid="tv-new-create"
            className="tv-rail-new-btn"
            onClick={createTable}
            disabled={!newName.trim() || creating}
            title="Create table"
          >＋</button>
        </div>
      </div>

      {/* Main panel */}
      <div className="tv-main">
        {!selected ? (
          <div className="tv-hero">
            <div className="tv-hero-icon">🗂️</div>
            <h2>Your data lives here</h2>
            <p>
              Create a table to track anything — leads, orders, tickets, candidates.
              Import a CSV, edit records in a grid, and then let pipelines automate
              the rest. Collections you build on the Pipeline canvas show up here too.
            </p>
            <p className="tv-hero-hint">Start by typing a table name in the left panel.</p>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="tv-toolbar">
              <input
                className="tv-title-input"
                defaultValue={selected.data?.label || ''}
                key={selected.id}
                onBlur={e => renameTable(e.target.value)}
                title="Table name (click to rename)"
              />
              <span className="tv-count">{records.length} record{records.length !== 1 ? 's' : ''}</span>
              <div className="tv-toolbar-spacer" />
              <input
                className="tv-search"
                placeholder="Search records…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button className={`btn-secondary tv-tb-btn ${showFields ? 'active' : ''}`} data-testid="tv-fields-toggle" onClick={() => setShowFields(v => !v)}>
                ⚙️ Fields
              </button>
              <button className="btn-secondary tv-tb-btn" onClick={() => fileRef.current?.click()} data-testid="tv-import">
                📥 Import CSV
              </button>
              <button className="btn-secondary tv-tb-btn" onClick={exportCsv} disabled={records.length === 0 || fields.length === 0}>
                📤 Export
              </button>
              <button className="btn-secondary tv-tb-btn" data-testid="tv-forms" onClick={() => setShowForms(true)} disabled={fields.length === 0} title="Share a public form that adds records to this table">
                🔗 Forms
              </button>
              {onNavigate && (
                <button
                  className="btn-secondary tv-tb-btn tv-automate"
                  data-testid="tv-automate"
                  title="Open the pipeline canvas to automate this table (add a Trigger watching it)"
                  onClick={() => onNavigate('pipeline')}
                >⚡ Automate</button>
              )}
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onCsvPicked} />
            </div>

            {/* View switcher */}
            <div className="tv-views" data-testid="tv-views">
              <button
                className={`tv-view-tab ${!activeViewId ? 'active' : ''}`}
                onClick={() => setActiveViewId(null)}
              >▦ All records</button>
              {views.map(v => (
                <button
                  key={v.id}
                  data-testid={`tv-view-${v.id}`}
                  className={`tv-view-tab ${activeViewId === v.id ? 'active' : ''}`}
                  onClick={() => setActiveViewId(v.id)}
                  onDoubleClick={() => setEditorView(v)}
                  title="Double-click to edit this view"
                >
                  {v.type === 'kanban' ? '📌' : '▦'} {v.name}
                </button>
              ))}
              <button
                className="tv-view-tab tv-view-new"
                data-testid="tv-view-add"
                onClick={() => setEditorView({ name: '', type: 'grid', filters: [], hiddenFieldIds: [] })}
                disabled={fields.length === 0}
                title={fields.length === 0 ? 'Add fields first' : 'Create a saved view'}
              >＋ View</button>
              {activeView && (
                <button className="tv-view-edit" onClick={() => setEditorView(activeView)} title="Edit this view">✎</button>
              )}
            </div>

            {/* Fields editor */}
            {showFields && (
              <div className="tv-fields-panel" data-testid="tv-fields-panel">
                <div className="cm-fields-list">
                  {fields.map(f => (
                    <FieldRow
                      key={f.id}
                      field={f}
                      allCollections={otherCollections}
                      onUpdate={u => updateField(f.id, u)}
                      onDelete={() => deleteField(f.id)}
                    />
                  ))}
                </div>
                <div className="cm-add-field-row">
                  <input
                    data-testid="tv-add-field-name"
                    className="cm-add-field-name"
                    placeholder="New field name…"
                    value={nfName}
                    onChange={e => setNfName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addField()}
                  />
                  <select className="cm-add-field-type" value={nfType} onChange={e => setNfType(e.target.value)}>
                    {[
                      { value: 'text', label: '📝 Text' }, { value: 'number', label: '🔢 Number' },
                      { value: 'select', label: '🔽 Select' }, { value: 'date', label: '📅 Date' },
                      { value: 'relation', label: '🔗 Relation' },
                    ].map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <button data-testid="tv-add-field" className="cm-add-field-btn" onClick={addField} disabled={!nfName.trim()}>
                    + Add field
                  </button>
                </div>
              </div>
            )}

            {/* Body: hero / kanban / grid */}
            {fields.length === 0 ? (
              <div className="tv-hero">
                <div className="tv-hero-icon">⚙️</div>
                <h2>Define your columns</h2>
                <p>Add your first field above (open <strong>⚙️ Fields</strong> if it's closed) — e.g. Name, Email, Status — then start adding records or import a CSV.</p>
              </div>
            ) : isKanban ? (
              <KanbanBoard
                records={visibleRecords}
                fields={shownFields}
                groupBy={activeView.groupBy}
                onMove={(rid, val) => patchRecord(rid, activeView.groupBy, val)}
                onOpen={setDetailId}
              />
            ) : (
              <div className="tv-grid-wrap">
                <table className="cm-table tv-table">
                  <thead>
                    <tr>
                      <th className="cm-th cm-th-num">#</th>
                      {shownFields.map(f => (
                        <th key={f.id} className="cm-th tv-th-sortable" onClick={() => toggleSort(f.id)} title="Click to sort">
                          <span className="cm-th-icon">{typeIcon(f.type)}</span>
                          {f.name}
                          {sort?.fieldId === f.id && <span className="tv-sort-arrow">{sort.dir === 1 ? ' ↑' : ' ↓'}</span>}
                        </th>
                      ))}
                      <th className="cm-th cm-th-del"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRecords.length === 0 && !addingRec && (
                      <tr>
                        <td colSpan={shownFields.length + 2} className="cm-td-empty">
                          {search || activeView ? 'No records match this view/search.' : 'No records yet — click "+ Add record" below, or Import CSV.'}
                        </td>
                      </tr>
                    )}
                    {visibleRecords.map((r, idx) => {
                      const statusField = shownFields.find(f => f.name.toLowerCase() === 'status' && f.type === 'select');
                      const statusVal = statusField ? r.values?.[statusField.id] : null;
                      const statusColor = statusVal ? STATUS_COLORS[statusVal] : null;
                      return (
                        <tr
                          key={r.id}
                          className={`cm-tr tv-tr ${detailId === r.id ? 'tv-tr-active' : ''}`}
                          style={statusColor ? { borderLeft: `3px solid ${statusColor}` } : {}}
                          onContextMenu={e => { e.preventDefault(); setRecordCtx({ record: r, x: e.clientX, y: e.clientY }); }}
                        >
                          <td className="cm-td cm-td-num tv-td-open" onClick={() => setDetailId(r.id)} title="Open record">{idx + 1}</td>
                          {shownFields.map(f => (
                            <td key={f.id} className="cm-td">
                              <RecordCell
                                field={f}
                                value={r.values?.[f.id]}
                                allCollections={collections}
                                recordsByCol={recordsByCol}
                                onChange={val => patchRecord(r.id, f.id, val)}
                              />
                            </td>
                          ))}
                          <td className="cm-td cm-td-del">
                            <button className="tv-open-btn" onClick={() => setDetailId(r.id)} title="Open record">↗</button>
                          </td>
                        </tr>
                      );
                    })}
                    {addingRec && (
                      <tr data-testid="tv-draft-row" className="cm-tr cm-tr-new">
                        <td className="cm-td cm-td-num">＋</td>
                        {shownFields.map(f => (
                          <td key={f.id} className="cm-td">
                            <RecordCell
                              field={f}
                              value={newRec[f.id]}
                              allCollections={collections}
                              recordsByCol={recordsByCol}
                              onChange={val => setNewRec(p => ({ ...p, [f.id]: val }))}
                            />
                          </td>
                        ))}
                        <td className="cm-td cm-td-del">
                          <button data-testid="tv-save-record" className="cm-rec-save-btn" onClick={commitNewRecord} title="Save record" aria-label="Save record">✓</button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="cm-records-actions">
                  {addingRec ? (
                    <span className="cm-add-rec-hint">
                      Fill the new row, then click the green ✓ to save it.{' '}
                      <button className="cm-add-rec-cancel" onClick={() => { setAddingRec(false); setNewRec({}); }}>Cancel</button>
                    </span>
                  ) : (
                    <button data-testid="tv-add-record" className="cm-add-rec-btn" onClick={() => { setNewRec({}); setAddingRec(true); }}>
                      + Add record
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Record detail drawer */}
      {detailRecord && (
        <RecordDetail
          record={detailRecord}
          fields={fields}
          allCollections={collections}
          recordsByCol={recordsByCol}
          manualTriggers={manualTriggers}
          onRun={runTriggerOnRecord}
          onChange={(fId, val) => patchRecord(detailRecord.id, fId, val)}
          onDelete={deleteRecord}
          onClose={() => setDetailId(null)}
        />
      )}

      {recordCtx && (
        <RecordCtxMenu
          x={recordCtx.x} y={recordCtx.y}
          record={recordCtx.record}
          fields={fields}
          allCollections={collections}
          onLink={(field) => { setLinkPicker({ record: recordCtx.record, field }); setRecordCtx(null); }}
          onDelete={deleteRecord}
          onClose={() => setRecordCtx(null)}
        />
      )}
      {linkPicker && (
        <LinkPicker
          field={linkPicker.field}
          allCollections={collections}
          recordsByCol={recordsByCol}
          currentValue={linkPicker.record.values?.[linkPicker.field.id]}
          onPick={(val) => patchRecord(linkPicker.record.id, linkPicker.field.id, val)}
          onClose={() => setLinkPicker(null)}
        />
      )}

      {importParsed && (
        <ImportModal
          parsed={importParsed}
          fields={fields}
          onImport={runImport}
          onClose={() => setImportParsed(null)}
        />
      )}

      {editorView && (
        <ViewEditor
          view={editorView}
          fields={fields}
          onSave={saveView}
          onDelete={deleteView}
          onClose={() => setEditorView(null)}
        />
      )}

      {showForms && selected && (
        <FormsModal
          workspaceId={workspaceId}
          collection={selected}
          fields={fields}
          onClose={() => setShowForms(false)}
        />
      )}
    </div>
  );
}
